import { oidcAPI } from 'api';
import {
  all,
  call,
  put,
  SagaReturnType,
  takeLeading,
} from 'redux-saga/effects';
import * as R from 'remeda';
import { sagaWrapper } from 'store/helpers';
import { ActionType } from 'typesafe-actions';

import * as userActions from './actions';
import * as constants from './constants';

const LOCAL_STORAGE_ITEM = 'token';

function* startLoginSaga(action: ActionType<typeof userActions.startLogin>) {
  function* saga() {
    const user: SagaReturnType<typeof oidcAPI.getUser> = yield call(
      oidcAPI.getUser,
    );

    if (!R.isDefined(user)) {
      localStorage.removeItem(LOCAL_STORAGE_ITEM);
      yield put(userActions.setToken(null));

      // Carry the deep-link target through the IdP roundtrip via OIDC `state`.
      // startVerifySaga reads it back from signinRedirectCallback().state.
      const { redirectTo } = action.payload;
      yield call(oidcAPI.signinRedirect, {
        state: R.isDefined(redirectTo) ? { redirectTo } : undefined,
      });
      return;
    }

    const bearer = user.access_token;
    localStorage.setItem(LOCAL_STORAGE_ITEM, bearer);

    yield put(userActions.setToken(bearer));
  }

  yield call(sagaWrapper, { saga, type: action.type });
}

function* startLogoutSaga(action: ActionType<typeof userActions.startLogout>) {
  function* saga() {
    localStorage.removeItem(LOCAL_STORAGE_ITEM);
    yield put(userActions.setToken(null));

    yield call(oidcAPI.signoutRedirect);
  }

  yield call(sagaWrapper, { saga, type: action.type });
}

function* startVerifySaga(action: ActionType<typeof userActions.startVerify>) {
  function* saga() {
    const user: SagaReturnType<typeof oidcAPI.signinRedirectCallback> =
      yield call(oidcAPI.signinRedirectCallback);
    const bearer = user.access_token;
    localStorage.setItem(LOCAL_STORAGE_ITEM, bearer);

    // Restore the deep-link target encoded in OIDC `state` by startLoginSaga.
    // SigninOidcPage reads `postLoginRedirect` after the token is set and
    // navigates there, falling back to START_PAGE if absent or unsafe.
    const state = (user as { state?: { redirectTo?: unknown } }).state;
    const redirectTo =
      typeof state?.redirectTo === 'string' ? state.redirectTo : null;
    yield put(userActions.setPostLoginRedirect(redirectTo));

    yield put(userActions.setToken(bearer));
  }

  yield call(sagaWrapper, { saga, type: action.type });
}

export function* watcher() {
  yield all([
    takeLeading(constants.START_LOGIN, startLoginSaga),
    takeLeading(constants.START_VERIFY, startVerifySaga),
    takeLeading(constants.START_LOGOUT, startLogoutSaga),
  ]);
}
